import React from 'react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Check, Store, AlertCircle, Loader2 } from 'lucide-react';
import { CampaignData } from '@/hooks/sms/campaign/types';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useSmsStatus } from '@/context/SmsStatusContext';
import { useAuth } from '@/context/auth';
import { hasSuperAdminAccess, hasOrgAdminAccess } from '@/context/auth/types';
import { toast } from '@/hooks/use-toast';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';

interface RestaurantSelectorProps {
  userRestaurants: any[];
  selectedRestaurants: string[];
  updateCampaignData: (data: Partial<CampaignData>) => void;
}

const RestaurantSelector: React.FC<RestaurantSelectorProps> = ({
  userRestaurants,
  selectedRestaurants,
  updateCampaignData
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [restaurantSmsStatus, setRestaurantSmsStatus] = React.useState<Map<string, boolean>>(new Map());
  const [isLoadingStatus, setIsLoadingStatus] = React.useState<Map<string, boolean>>(new Map());
  const [isEnablingSms, setIsEnablingSms] = React.useState<Map<string, boolean>>(new Map());
  
  const { user } = useAuth();
  const { 
    checkRestaurantSmsStatus,
    canEnableSms,
    enableRestaurantSms,
    restaurantSmsStatus: contextRestaurantStatus
  } = useSmsStatus();

  React.useEffect(() => {
    if (isOpen) {
      loadSmsStatus();
    }
  }, [isOpen]);

  React.useEffect(() => {
    if (contextRestaurantStatus.size > 0) {
      const newStatusMap = new Map(restaurantSmsStatus);
      for (const [restaurantId, status] of contextRestaurantStatus.entries()) {
        newStatusMap.set(restaurantId, status);
      }
      setRestaurantSmsStatus(newStatusMap);
    }
  }, [contextRestaurantStatus]);

  const loadSmsStatus = async () => {
    const newLoadingMap = new Map();
    const newStatusMap = new Map(restaurantSmsStatus);
    
    for (const restaurant of userRestaurants) {
      if (!restaurantSmsStatus.has(restaurant.id)) {
        newLoadingMap.set(restaurant.id, true);
        setIsLoadingStatus(new Map(newLoadingMap));
        
        try {
          const status = await checkRestaurantSmsStatus(restaurant.id);
          newStatusMap.set(restaurant.id, status);
        } catch (error) {
          console.error(`Error checking SMS status for ${restaurant.name}:`, error);
          toast({
            title: "Error checking SMS status",
            description: `Could not verify SMS status for ${restaurant.name}`,
            variant: "destructive"
          });
        } finally {
          newLoadingMap.set(restaurant.id, false);
          setIsLoadingStatus(new Map(newLoadingMap));
        }
      }
    }
    
    setRestaurantSmsStatus(newStatusMap);
  };

  const handleRestaurantChange = (restaurantId: string) => {
    let newSelection = [...selectedRestaurants];
    
    if (restaurantId === 'all') {
      newSelection = ['all'];
    } else {
      if (newSelection.includes('all')) {
        newSelection = newSelection.filter(id => id !== 'all');
      }
      
      if (newSelection.includes(restaurantId)) {
        if (newSelection.length > 1) {
          newSelection = newSelection.filter(id => id !== restaurantId);
        } else {
          newSelection = ['all'];
        }
      } else {
        newSelection.push(restaurantId);
      }
    }
    
    updateCampaignData({ selectedRestaurants: newSelection });
  };

  const handleEnableSms = async (e: React.MouseEvent, restaurantId: string, restaurantName: string) => {
    e.stopPropagation();
    
    if (!user || !canEnableSms(user, restaurantId)) {
      toast({
        title: "Permission Denied",
        description: "You don't have permission to enable SMS for this restaurant",
        variant: "destructive"
      });
      return;
    }
    
    const enableMap = new Map(isEnablingSms);
    enableMap.set(restaurantId, true);
    setIsEnablingSms(enableMap);
    
    try {
      const success = await enableRestaurantSms(restaurantId);
      
      if (success) {
        const statusMap = new Map(restaurantSmsStatus);
        statusMap.set(restaurantId, true);
        setRestaurantSmsStatus(statusMap);
        
        toast({
          title: "SMS Enabled",
          description: `SMS has been enabled for ${restaurantName}`,
        });
      }
    } catch (error) {
      console.error("Error enabling SMS:", error);
      toast({
        title: "Error Enabling SMS",
        description: error instanceof Error ? error.message : "An unexpected error occurred",
        variant: "destructive"
      });
    } finally {
      const enableMap = new Map(isEnablingSms);
      enableMap.set(restaurantId, false);
      setIsEnablingSms(enableMap);
    }
  };

  const getDisplayValue = () => {
    if (selectedRestaurants.includes('all')) {
      return 'All Restaurants';
    }
    
    if (selectedRestaurants.length === 1) {
      const restaurant = userRestaurants.find(r => r.id === selectedRestaurants[0]);
      return restaurant ? restaurant.name : 'Select Restaurant';
    }
    
    return `${selectedRestaurants.length} Restaurants Selected`;
  };

  const hasDisabledSmsRestaurant = () => {
    if (selectedRestaurants.includes('all')) {
      return userRestaurants.some(r => 
        restaurantSmsStatus.has(r.id) && !restaurantSmsStatus.get(r.id)
      );
    }
    
    return selectedRestaurants.some(id => 
      restaurantSmsStatus.has(id) && !restaurantSmsStatus.get(id)
    );
  };

  const getDisabledSmsRestaurants = () => {
    if (selectedRestaurants.includes('all')) {
      return userRestaurants
        .filter(r => restaurantSmsStatus.has(r.id) && !restaurantSmsStatus.get(r.id))
        .map(r => r.name);
    }
    
    return userRestaurants
      .filter(r => selectedRestaurants.includes(r.id) && 
        restaurantSmsStatus.has(r.id) && !restaurantSmsStatus.get(r.id))
      .map(r => r.name);
  };

  const canUserEnableSms = user && 
    (hasSuperAdminAccess(user.role) || hasOrgAdminAccess(user.role));

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <Label>Select Restaurants</Label>
        {hasDisabledSmsRestaurant() && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center text-amber-500 text-xs">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  SMS not enabled for all selections
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>SMS is not enabled for: {getDisabledSmsRestaurants().join(', ')}</p>
                {canUserEnableSms ? (
                  <p className="text-xs mt-1">You can enable SMS within the selector</p>
                ) : (
                  <p className="text-xs mt-1">Contact an admin to enable SMS</p>
                )}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      
      <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <Button 
              variant="outline" 
              className={`w-full justify-between ${hasDisabledSmsRestaurant() ? 'border-amber-500' : ''}`}
              type="button"
            >
              <div className="flex items-center gap-2">
                <Store className="h-4 w-4" />
                <span>{getDisplayValue()}</span>
              </div>
            </Button>
          </PopoverTrigger>
          <PopoverContent 
            className="p-0 w-[350px] bg-background" 
            align="start"
          >
            <div className="py-2">
              <div className="px-2 py-1.5 text-sm font-semibold">Select Restaurants</div>
              <div className="-mx-1 my-1 h-px bg-muted"></div>
              <div 
                className="relative flex items-center justify-between rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none cursor-pointer hover:bg-accent hover:text-accent-foreground"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRestaurantChange('all');
                  setIsOpen(false);
                }}
              >
                <div className="flex items-center">
                  <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                    {selectedRestaurants.includes('all') && (
                      <Check className="h-4 w-4" />
                    )}
                  </span>
                  All Restaurants
                </div>
                
                <div className="text-xs">
                  {userRestaurants.some(r => 
                    isLoadingStatus.get(r.id) || !restaurantSmsStatus.has(r.id)
                  ) && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
                  
                  {!userRestaurants.some(r => 
                    isLoadingStatus.get(r.id) || !restaurantSmsStatus.has(r.id)
                  ) && (
                    userRestaurants.some(r => !restaurantSmsStatus.get(r.id)) ? (
                      <Badge variant="outline" className="text-amber-500 border-amber-500">
                        Some SMS Disabled
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-green-500 border-green-500">
                        All SMS Enabled
                      </Badge>
                    )
                  )}
                </div>
              </div>
              <div className="-mx-1 my-1 h-px bg-muted"></div>
              {userRestaurants.map(restaurant => (
                <div
                  key={restaurant.id}
                  className="relative flex items-center justify-between rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none cursor-pointer hover:bg-accent hover:text-accent-foreground"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRestaurantChange(restaurant.id);
                  }}
                >
                  <div className="flex items-center">
                    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                      {selectedRestaurants.includes(restaurant.id) && (
                        <Check className="h-4 w-4" />
                      )}
                    </span>
                    {restaurant.name}
                  </div>
                  
                  <div className="text-xs">
                    {isLoadingStatus.get(restaurant.id) && (
                      <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                    )}
                    
                    {!isLoadingStatus.get(restaurant.id) && restaurantSmsStatus.has(restaurant.id) && (
                      restaurantSmsStatus.get(restaurant.id) ? (
                        <Badge variant="outline" className="text-green-500 border-green-500">
                          SMS Enabled
                        </Badge>
                      ) : (
                        <div className="flex items-center">
                          <Badge variant="outline" className="text-amber-500 border-amber-500 mr-1.5">
                            SMS Disabled
                          </Badge>
                          
                          {canUserEnableSms && canEnableSms(user, restaurant.id) && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="h-6 text-[10px] border-green-500 text-green-600 hover:bg-green-50"
                              onClick={(e) => handleEnableSms(e, restaurant.id, restaurant.name)}
                              disabled={isEnablingSms.get(restaurant.id)}
                            >
                              {isEnablingSms.get(restaurant.id) ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                'Enable'
                              )}
                            </Button>
                          )}
                        </div>
                      )
                    )}
                  </div>
                </div>
              ))}
            </div>
          </PopoverContent>
        </Popover>
        
        <p className="text-sm text-muted-foreground">Choose which restaurants to include in your campaign</p>
      
      {hasDisabledSmsRestaurant() && (
        <div className="mt-2 p-2 border border-amber-200 bg-amber-50 rounded-md text-sm text-amber-800">
          <p className="flex items-center">
            <AlertCircle className="h-4 w-4 mr-1.5 flex-shrink-0" />
            <span>
              {canUserEnableSms ? (
                <>SMS is not enabled for some selected restaurants. Enable SMS to send campaigns.</>
              ) : (
                <>SMS is not enabled for some selected restaurants. Contact an admin to enable SMS.</>
              )}
            </span>
          </p>
        </div>
      )}
    </div>
  );
};

export default RestaurantSelector;
